<template>
  <!-- overflow-y-auto + my-auto: when the on-screen keyboard shrinks the web view, the card
       scrolls instead of pushing Confirm out of reach (same pattern as KioskEventSelector) -->
  <div
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
    @click.self="$emit('cancel')"
  >
    <div class="bg-white rounded-xl shadow-xl max-w-md w-full p-6 flex flex-col items-center gap-6 my-auto">
      <!-- Scale Icon -->
      <span
        class="flex items-center justify-center"
        style="color: var(--team-primary);"
      >
        <!-- Font Awesome Pro 5 "weight" (light), licensed; inlined since the kiosk doesn't load FA -->
        <svg class="w-12 h-12" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
          <path d="M448 64h-64.81C353.95 25.38 308.07 0 256 0s-97.95 25.38-127.19 64H64C28.71 64 0 92.71 0 128v320c0 35.29 28.71 64 64 64h384c35.29 0 64-28.71 64-64V128c0-35.29-28.71-64-64-64zM256 32c70.69 0 128 57.31 128 128s-57.31 128-128 128-128-57.31-128-128S185.31 32 256 32zm224 416c0 17.67-14.33 32-32 32H64c-17.67 0-32-14.33-32-32V128c0-17.67 14.33-32 32-32h45.56C100.94 115.63 96 137.22 96 160c0 88.22 71.78 160 160 160s160-71.78 160-160c0-22.78-4.94-44.37-13.56-64H448c17.67 0 32 14.33 32 32v320zM256 256c26.47 0 48-21.53 48-48 0-13.92-6.05-26.36-15.54-35.13l30.24-70.57c3.48-8.11-.28-17.52-8.41-21-8.08-3.48-17.52.27-21 8.41l-30.26 70.6C223.57 158 208 188.65 208 208c0 26.47 21.53 48 48 48zm0-64c8.84 0 16 7.16 16 16s-7.16 16-16 16-16-7.16-16-16 7.16-16 16-16z"/>
        </svg>
      </span>

      <!-- Text Content -->
      <div class="flex flex-col gap-2 text-center">
        <h3 class="text-xl font-bold text-gray-900">
          {{ mode === 'standalone' ? 'Log weight' : 'Log weight to check in' }}
        </h3>
        <p v-if="wrestlerName" class="text-base font-semibold text-gray-900">
          {{ wrestlerName }}
        </p>
        <p class="text-base font-medium text-gray-600 leading-6">
          {{ mode === 'standalone' ? 'Enter your weight.' : 'Enter your weight to finish checking in.' }}
        </p>
        <p v-if="lastWeight" class="text-sm text-gray-500">
          Last logged: {{ lastWeight }} {{ lastWeightUnits }}
        </p>
      </div>

      <!-- Date / Time (display only — kiosk weigh-ins are always "now") -->
      <div class="flex flex-col gap-2 w-full">
        <span class="flex items-center gap-2 w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-700">
          <svg class="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <span>Today</span>
        </span>
        <span class="flex items-center gap-2 w-full bg-gray-100 rounded-lg px-4 py-3 text-gray-700">
          <svg class="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <span>{{ currentTime }}</span>
        </span>
      </div>

      <!-- Weight Input -->
      <div class="w-full">
        <div
          class="relative border-2 rounded-lg px-4 py-3"
          style="border-color: var(--team-primary);"
        >
          <span class="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-600">
            Weight ({{ units === 'lbs' ? 'lb' : 'kg' }})
          </span>
          <input
            ref="weightInput"
            :value="weight"
            type="tel"
            inputmode="decimal"
            autocomplete="off"
            class="w-full text-2xl font-medium text-gray-900 focus:outline-none"
            placeholder="0.0"
            @input="handleInput"
            @keydown.enter="confirm"
          />
        </div>
        <!-- Unit toggle -->
        <div class="flex justify-end gap-2 mt-2">
          <button
            v-for="unit in ['lbs', 'kg']"
            :key="unit"
            class="px-3 py-1 rounded-lg text-sm font-semibold border transition-colors"
            :style="unit === units
              ? { backgroundColor: 'var(--team-primary)', color: 'var(--team-text)', borderColor: 'var(--team-primary)' }
              : { borderColor: '#d1d5db', color: '#4b5563' }"
            @click="units = unit"
          >
            {{ unit }}
          </button>
        </div>
      </div>

      <!-- Buttons -->
      <div class="flex flex-col gap-4 w-52">
        <button
          @click="confirm"
          :disabled="!isValid"
          class="w-full px-6 py-2 rounded-lg font-semibold text-base text-white transition-opacity"
          :class="isValid ? '' : 'opacity-50 cursor-not-allowed'"
          :style="confirmButtonStyle"
        >
          Confirm
        </button>
        <button
          @click="$emit('cancel')"
          class="w-full px-6 py-2 rounded-lg font-semibold text-base border transition-colors hover:bg-gray-50"
          style="border-color: var(--team-primary); color: var(--team-primary);"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Teams rarely mix units, so the last choice is remembered on this device
const UNITS_STORAGE_KEY = 'wiq_kiosk_weight_units';

function loadPreferredUnits() {
  try {
    const stored = localStorage.getItem(UNITS_STORAGE_KEY);
    return stored === 'kg' ? 'kg' : 'lbs';
  } catch (e) {
    return 'lbs';
  }
}

function savePreferredUnits(units) {
  try {
    localStorage.setItem(UNITS_STORAGE_KEY, units);
  } catch (e) {
    // localStorage unavailable (private mode, etc) - not worth surfacing
  }
}

export default {
  name: 'KioskWeightModal',
  props: {
    // 'check-in' gates a pending check-in; 'standalone' logs a weight on its own
    mode: {
      type: String,
      default: 'check-in',
    },
    wrestlerName: {
      type: String,
      default: '',
    },
    // From the wrestler payload's counter-cached latest_weight* columns
    lastWeight: {
      type: Number,
      default: null,
    },
    lastWeightUnits: {
      type: String,
      default: null,
    },
  },
  emits: ['confirm', 'cancel', 'focused'],
  data() {
    return {
      weight: '',
      units: loadPreferredUnits(),
      currentTime: moment().format('h:mm a'),
    };
  },
  computed: {
    parsedWeight() {
      return parseFloat(this.weight);
    },
    isValid() {
      return !Number.isNaN(this.parsedWeight) && this.parsedWeight > 0 && this.parsedWeight < 1000;
    },
    confirmButtonStyle() {
      if (this.isValid) {
        return {
          backgroundColor: 'var(--team-primary)',
        };
      }
      return {
        backgroundColor: '#bcccdc',
      };
    },
  },
  mounted() {
    // Focus the weight input when modal opens
    this.$nextTick(() => {
      if (this.$refs.weightInput) {
        this.$refs.weightInput.focus();
        // The iOS kiosk app won't raise the keyboard for a focus() it didn't see a tap for;
        // the parent asks the native bridge to show it
        this.$emit('focused');
      }
    });
  },
  methods: {
    // type="tel" accepts anything, so keep it to a decimal number: digits and one ".", with a
    // "," (decimal key in comma locales) read as "."
    handleInput(event) {
      let value = event.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
      const firstDot = value.indexOf('.');
      if (firstDot !== -1) {
        value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, '');
      }
      this.weight = value;
      event.target.value = value;
    },
    confirm() {
      if (!this.isValid) return;

      savePreferredUnits(this.units);
      this.$emit('confirm', {
        weight: this.parsedWeight,
        units: this.units,
      });
    },
  },
};
</script>
